import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, EyeOff, Loader2, Sparkles, Undo2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/shared/lib/utils";
import { toast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { Skeleton } from "@/shared/ui/skeleton";

import {
  ACQ_PRODUCT_NONE,
  acqService,
  type AcqCadence,
  type AcqCatalogItem,
  type AcqCatalogView,
  type AcqPrefLevel,
  type AcqScene,
} from "../api/acqService";
import {
  partitionCatalog,
  partitionCatalogByProduct,
  productKeywords,
  productLevelOf,
  scenesOf,
  useCatalog,
} from "../model/useCatalog";
import { todayFullFromView } from "../model/todayFull";
import { useDiscoverRun } from "../model/useDiscoverRun";
import { useProductParam } from "../model/useProductParam";
import { useTenantProducts } from "../model/useTenantProducts";
import { AcqShell } from "./AcqShell";
import { DiscoveryTypeCard } from "./components/DiscoveryTypeCard";
import { KeywordsDrawer, type KeywordsDrawerTopic } from "./components/KeywordsDrawer";
import { ProductTabs, type ProductTabItem } from "./components/ProductTabs";
import { PushSettings } from "./components/PushSettings";
import { RecommendBanner } from "./components/RecommendBanner";
import { SceneStrip } from "./components/SceneStrip";
import { SubscribeDialog } from "./components/SubscribeDialog";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 去哪里找 —— 发现目录(契约 §12.8)
 * ═══════════════════════════════════════════════════════════════════════════
 * 老板拍板的两件事同时要成立:
 *   ① **展示种类之多**(15 种 = 这个产品真的够宽);
 *   ② **按行业告诉他该去哪儿找**(不然 15 张卡就是 15 个"你自己挑吧")。
 * 所以页面是「分区 + 推荐档」而不是一面平铺的卡墙:主推 / 可试在上面,
 * 其余折进「更多」,还没通的单独一区并标「即将开通」。
 *
 * ★ 新用户**没有订阅这一说**:先在这儿手动跑一次(扣分,价写在按钮上),
 *   觉得有用再把那一种变成「订阅自动找」。订阅不是入口,是结果。
 * ★ 这一页**没有 `max-w-3xl`**:它是卡墙,要用满屏(契约 §12.10)。
 * ═══════════════════════════════════════════════════════════════════════════
 */
export default function DiscoverCatalogPage({
  initialView,
}: { initialView?: AcqCatalogView | null } = {}) {
  const { view, loading, error, recommending, reload, recommend, update } = useCatalog(initialView);
  const navigate = useNavigate();
  /**
   * 1006:今日商机已满(api 2bf9d257 契约)。点之前按今日接口的 `total >= cap` 判,满了禁用付费线的
   * 「发现一次」并在按钮旁写「今日商机已满 N 条」;发起时 api 409 回的那句人话(hook 记下)兜底。
   * 今日接口拉挂了 / 老后端没 total ⇒ 不判,行为不变。每轮跑完随目录一起重拉。
   */
  const [todayFullByView, setTodayFullByView] = useState<string | null>(null);
  const refreshToday = useCallback(async () => {
    try {
      setTodayFullByView(todayFullFromView(await acqService.today({ size: 1 })));
    } catch {
      /* 拉不到不判:不能拿猜的数把能跑的线锁死 */
    }
  }, []);
  useEffect(() => {
    void refreshToday();
  }, [refreshToday]);
  const reloadAll = useCallback(() => {
    void refreshToday();
    return reload();
  }, [reload, refreshToday]);
  const { runningType, elapsedSec, run, resume, todayFullReason } = useDiscoverRun(reloadAll);
  const todayFull = todayFullByView ?? todayFullReason;
  const { products, nameOf } = useTenantProducts();
  /** 选中的产品页签(契约 §16.2 / §16.7):与 URL `?product=` 双向同步,四页共用一根。 */
  const [urlProduct, setProduct] = useProductParam();
  /** 点亮的那个场景(契约 §16.1)。只高亮不筛 —— 见 `SceneStrip` 的注释。 */
  const [activeScene, setActiveScene] = useState<string | null>(null);
  /**
   * 「按行业通用推荐看」(契约 §17.2 零场景兜底的第一个按钮)。
   *
   * ★ 它只换**分区**那一件事:产品页签还选着、卡上的词还是这个产品的词组、
   *   「发现一次」还是围着这个产品跑 —— 变的只是"按什么给这 17 张卡排序"。
   *   ★ 不改 URL:它是"这一眼怎么看",不是"我选了别的产品"。分享出去的链接
   *   仍然是那个产品(而对方多半有场景 —— 这个开关本来就是给没场景的人用的)。
   */
  const [globalFallback, setGlobalFallback] = useState(false);

  const [moreOpen, setMoreOpen] = useState(false);
  const [editing, setEditing] = useState<AcqCatalogItem | null>(null);
  const [subscribing, setSubscribing] = useState<AcqCatalogItem | null>(null);
  const [saving, setSaving] = useState(false);

  const items = useMemo(() => view?.items ?? [], [view]);
  const scenes = useMemo<AcqScene[]>(() => view?.scenes ?? [], [view]);

  /**
   * 0927 异步化:目录里有一张卡还在跑(刷新页面 / 同事在别处点的)⇒ 接上它的轮询。
   * ★ 不重新 POST、不跳页:这只是把「正在找」和终态提示接回面上。`resume` 自己守「已经在跟一轮」的闸,
   *   所以每次目录重拉都可以放心再调一遍。
   */
  useEffect(() => {
    const busy = items.find((i) => i.lastRun?.status === "RUNNING");
    if (busy?.lastRun) resume(busy.type, busy.lastRun, { archetype: busy.archetype });
  }, [items, resume]);
  /**
   * 产品页签的数据(契约 §16.2:`productSummary`,按后端顺序)。
   * ★ 后端还没跟上时**退到产品树**:名字有、数没有(`count: null`)——
   *   报一个前端自己算的数是第二份真相,而这一排页签正是要按它做决定的。
   */
  const tabs = useMemo<ProductTabItem[]>(() => {
    const summary = view?.productSummary;
    if (summary && summary.length > 0) {
      return summary.map((s) => ({ code: s.code, name: s.name, count: s.openCount }));
    }
    return products.map((p) => ({ code: p.nodeCode, name: p.name, count: null }));
  }, [view, products]);

  /**
   * 这一页真正认的那个产品。
   *
   * ★★ 两种值要**归一成「全部」**,否则整页会灰掉而没有任何一处说为什么:
   *   ① `__none__`(从今日 / 线索库带过来的「未指定产品」)—— 那是个**筛卡**的档,
   *      「为没绑产品的卡去发现一次」不成立:它没有词、没有场景,17 张卡会全落「更多」
   *      且「发现一次」全禁用;
   *   ② **不在这个租户产品树里的码**(分享出来的老链接 / 产品下架了)——
   *      同样会全灰,而且真点下去后端直接 400「没有这个在售产品」。
   * ★ URL 不动:归一只影响这一页怎么读它。改掉 URL 会让"从今日跳过来再跳回去"
   *   静默丢掉他选的那一档。
   */
  const product = useMemo(() => {
    if (!urlProduct || urlProduct === ACQ_PRODUCT_NONE) return "";
    return tabs.some((t) => t.code === urlProduct) ? urlProduct : "";
  }, [urlProduct, tabs]);

  /**
   * 分区:选中产品 ⇒ 按这个产品的场景重算(契约 §16.7);「全部」= 现状(全局档位)。
   */
  const parts = useMemo(
    () =>
      product && !globalFallback
        ? partitionCatalogByProduct(items, scenes, product)
        : partitionCatalog(items),
    [items, scenes, product, globalFallback]
  );

  /** 选中产品时只看挂在它下面的场景(契约 §16.7);「全部」看全部。 */
  const shownScenes = useMemo(
    () => (product ? scenes.filter((s) => (s.productCode ?? "") === product) : scenes),
    [scenes, product]
  );

  /**
   * ★★ `scene_id` **跨轮不稳定**(契约 §16.1 api 落地形状:每次 recommend 整租户覆盖式
   *   重编号)。所以它只在**同一份 `/catalog` 响应**里成立 —— 这里每次渲染都对着当前这份
   *   场景表验一遍,对不上就当没选。
   *   不验的后果:点了「重新推断」之后 `s2` 还亮着,而新的 `s2` 是另一个场景 ——
   *   页面会高亮一批**不相干**的卡,且没有任何一处报错。
   * ★ 同一条纪律的另一半:它**不进 URL、不进 localStorage**(见 `useProductParam` 只管 product)。
   */
  const activeSceneId = useMemo(
    () => (shownScenes.some((s) => s.id === activeScene) ? activeScene : null),
    [shownScenes, activeScene]
  );

  const onRun = useCallback(
    async (type: string) => {
      const archetype = items.find((i) => i.type === type)?.archetype ?? null;
      // 契约 §16.2:选中了产品就围绕它跑(本轮只用这个产品的词组,落卡默认绑它)。
      const got = await run(type, { productCode: product, archetype });
      // ★ 只有真出了单子才把人送去今日商机。没出单还跳过去,他会对着一页空的
      //   「今天没有够格的商机」以为刚才那次白跑了 —— 而正确的下一步是在这儿换一种再试。
      // ★ 跳过去**带着产品**(契约 §16.7 四页互跳带着):跑的是这个产品的一轮,
      //   落到一页「全部」上他会在一堆别的产品的卡里找自己刚跑出来的那几条。
      if (got) navigate(product ? `/ai-huoke?product=${encodeURIComponent(product)}` : "/ai-huoke");
    },
    [run, navigate, items, product]
  );

  const onToggleSubscribe = useCallback(
    async (item: AcqCatalogItem, next: boolean) => {
      // 开订阅要确认(它是"开始定期花钱");关订阅直接关(拦的是花钱那一侧)。
      if (next) {
        setSubscribing(item);
        return;
      }
      // ★ 契约 §17.3.1:产品页签下关的是**这个产品**的订阅(全局那格不动)。
      await update(item.type, { subscribed: false, ...(product ? { productCode: product } : {}) });
    },
    [update, product]
  );

  const confirmSubscribe = useCallback(
    async (cadence: AcqCadence) => {
      if (!subscribing) return;
      setSaving(true);
      await update(subscribing.type, {
        subscribed: true,
        cadence,
        // ★ 契约 §17.3.1:「全部」页签下仍然写全局(`subscribed_products_json` 空 = 全部词)。
        ...(product ? { productCode: product } : {}),
      });
      setSaving(false);
      setSubscribing(null);
    },
    [subscribing, update, product]
  );

  const saveKeywords = useCallback(
    async (keywords: string[], regions: string[], topic?: KeywordsDrawerTopic) => {
      if (!editing) return;
      setSaving(true);
      // 1009(契约 §5):评论原型连话题词 / 客户常问一起提交;keywords 照旧原样带上,不清。
      const row = await update(
        editing.type,
        topic ? { keywords, regions, ...topic } : { keywords, regions }
      );
      setSaving(false);
      // 1011:没保存上(例如竞品链接认不出,后端 400 逐条说原因,已弹 toast)⇒ 不关编辑框,
      //   用户粘的那十几条链接还在框里,改掉错的那条再点保存。
      if (row) setEditing(null);
    },
    [editing, update]
  );

  /**
   * 主推可调(契约 §13.6)。
   * ★ 就地 PUT 一行,**不重拉整页**:用户只是把一种从「可试」拨到「主推」,
   *   整页重拉会让 17 张卡闪一下、他刚才看的那张还可能跳到另一个区去。
   *   (分区本来就会变 —— 那是对的;闪整页不是。)
   */
  const onSetLevel = useCallback(
    async (item: AcqCatalogItem, level: AcqPrefLevel) => {
      await update(item.type, { level });
    },
    [update]
  );

  const onSetHidden = useCallback(
    async (item: AcqCatalogItem, hidden: boolean) => {
      await update(item.type, { hidden });
    },
    [update]
  );

  /**
   * 按产品改档(契约 §17.2 末句)。写 `productLevels`,**全局档一个字不动**。
   * ★ 没选产品时这一路根本不会被调到(菜单里那三项只在产品页签下出现)——
   *   但这里仍然守一道:一个不带 `productCode` 的 `level` 会被后端当成改全局档。
   */
  const onSetProductLevel = useCallback(
    async (item: AcqCatalogItem, level: AcqPrefLevel) => {
      if (!product) return;
      await update(item.type, { level, productCode: product });
    },
    [update, product]
  );

  /**
   * 「在「X」下不看」的**恢复**(契约 §18.1-2)。
   *
   * ★ 写的是 `level: null`(= 删掉 `product_levels_json` 里的那一格),**不是** `P2`:
   *   恢复的意思是"我没说过话",让这条线回到按场景重算 —— 写一个档进去等于替用户
   *   点了一下他没点过的菜单项,而下次重新推断也不会再覆盖它。
   * ★★ 回执要验:后端要是没把 `null` 当成删(或者忽略了它),那一行原样回来 ——
   *   卡不会重新出现、也没有任何一处报错,用户只会以为"恢复"这个按钮坏了。
   *   这一口是 0918 才接上的,**恰恰是最该验回执的那种**。
   */
  const onRestoreProductLevel = useCallback(
    async (item: AcqCatalogItem) => {
      if (!product) return;
      const row = await update(item.type, { level: null, productCode: product });
      if (row && productLevelOf(row, product) === "NA") {
        toast({
          title: "没恢复上",
          description: `后端仍把「${item.name}」记成在「${nameOf(product)}」下不看。`,
          variant: "destructive",
        });
      }
    },
    [update, product, nameOf]
  );

  const cardProps: CardProps = {
    onRun,
    elapsedSec,
    todayFull,
    productName: nameOf,
    onEditKeywords: setEditing,
    onToggleSubscribe: (i: AcqCatalogItem, n: boolean) => void onToggleSubscribe(i, n),
    onSetLevel: (i: AcqCatalogItem, l: AcqPrefLevel) => void onSetLevel(i, l),
    onSetHidden: (i: AcqCatalogItem, h: boolean) => void onSetHidden(i, h),
    onSetProductLevel: (i: AcqCatalogItem, l: AcqPrefLevel) => void onSetProductLevel(i, l),
    scenes,
    activeScene: activeSceneId,
    focusProduct: product,
  };

  return (
    <AcqShell subtitle="挑一个地方先找一次；觉得有用，再让它每天自己找">
      <div className="space-y-6">
        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
            <button
              type="button"
              onClick={() => void reload()}
              className="ml-2 font-semibold underline"
            >
              重试
            </button>
          </div>
        )}

        {view == null && loading && (
          <>
            <Skeleton className="h-20 w-full rounded-xl" />
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <Skeleton className="h-64 rounded-xl" />
              <Skeleton className="h-64 rounded-xl" />
              <Skeleton className="h-64 rounded-xl" />
            </div>
          </>
        )}

        {view != null && (
          <>
            <RecommendBanner
              view={view}
              recommending={recommending}
              onRecommend={() => void recommend()}
            />

            {/* ── 产品页签(契约 §16.2 / §16.7):这一舱的主轴 ─────────────────
                ★ 选中一个产品之后,下面的分区**按这个产品的场景重算** ——
                「全部」才是全局档位。老板原话:「我们这个仓应该都是根据产品来驱动的」。 */}
            <div className="space-y-2">
              <ProductTabs
                items={tabs}
                value={product}
                onChange={(code) => {
                  setProduct(code);
                  // ★ 换产品要把「按行业通用推荐看」放掉:它是上一个产品"没场景"时的一次
                  //   临时视角,留着会让下一个**有场景**的产品照样按全局档排 ——
                  //   而页面上没有任何一处说它还开着。
                  setGlobalFallback(false);
                  // ★ 换产品要把场景高亮放掉:上一个产品的场景在新页签下根本不在列表里,
                  //   留着它等于有一枚看不见的筛子亮着。
                  setActiveScene(null);
                }}
                countLabel="条在手"
              />
              {product && (
                <p className="text-2xs text-muted-foreground">
                  {globalFallback ? (
                    <>
                      正在按「{nameOf(product)}」看，但下面的分区用的是
                      <span className="font-semibold">行业通用推荐</span>
                      （这个产品还没读出场景）；「发现一次」仍然只用这个产品的词跑。
                    </>
                  ) : (
                    <>
                      正在按「{nameOf(product)}」看：下面的分区是按这个产品的场景重排的，
                      「发现一次」也只用这个产品的词跑。
                    </>
                  )}
                </p>
              )}
              {/* ── 「在「X」下不看」的那几种(契约 §18.1-2)────────────────────
                  ★★ 卡本身**一张都不渲染**(见 `partitionCatalogByProduct`),
                  所以这一行是它们**唯一**的出口 —— 没有它,用户再也找不回自己藏掉的东西。 */}
              {parts.productHidden.length > 0 && (
                <ProductHiddenBar
                  productName={nameOf(product)}
                  items={parts.productHidden}
                  onRestore={(i) => void onRestoreProductLevel(i)}
                  // ★ 全藏光了 ⇒ 这一页下面一张卡都没有。那时这一行是页面上**唯一**的解释,
                  //   得说成一句整话(而不是一条小字脚注),否则用户看到的就是一页空白。
                  emptyPage={
                    parts.p0.length +
                      parts.p1.length +
                      parts.more.length +
                      parts.hidden.length +
                      parts.comingSoon.length ===
                    0
                  }
                />
              )}
            </div>

            {/* ── 从你的资料里读出的获客场景(契约 §16.1)────────────────────
                ★ 选中产品且**一条场景都没有**时不留白 —— 走契约 §17.2 的兜底块。 */}
            {product && shownScenes.length === 0 ? (
              <ProductNoScenes
                productName={nameOf(product)}
                globalFallback={globalFallback}
                recommending={recommending}
                onSeeGlobal={() => setGlobalFallback(true)}
                onBackToProduct={() => setGlobalFallback(false)}
                onRecommendProduct={() =>
                  void recommend({ productCode: product, productName: nameOf(product) })
                }
              />
            ) : (
              <SceneStrip
                scenes={shownScenes}
                kbDocs={view.inferredFrom?.kbDocs ?? 0}
                activeId={activeSceneId}
                onPick={setActiveScene}
                countOf={(s) => items.filter((i) => scenesOf(i, [s]).length > 0).length}
                productName={nameOf}
              />
            )}

            <Section
              title="主推"
              hint={
                product && !globalFallback
                  ? "这几条线抓得到这个产品的场景"
                  : "按你的知识库和行业包，这几个地方最可能找得到人"
              }
              items={parts.p0}
              running={runningType}
              cardProps={cardProps}
            />
            <Section
              title="可试"
              hint={
                product && !globalFallback
                  ? "配了这个产品的词，但没挂在哪个场景上 —— 先跑一次看看"
                  : "有道理，但不一定是你的主战场 —— 先跑一次看看"
              }
              items={parts.p1}
              running={runningType}
              cardProps={cardProps}
            />

            {parts.more.length + parts.hidden.length > 0 && (
              <section>
                <button
                  type="button"
                  onClick={() => setMoreOpen((v) => !v)}
                  aria-expanded={moreOpen}
                  className="flex items-center gap-1 text-sm font-bold hover:text-primary"
                >
                  {moreOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  更多（{parts.more.length + parts.hidden.length}）
                  <span className="ml-1 text-2xs font-normal text-muted-foreground">
                    不推荐的也在这儿，卡上写了为什么
                    {/* ★ 折叠着也要报出"你藏了几种":否则用户找不回自己藏掉的东西 ——
                        而他记得的只是"我好像关过它",不记得关的是哪一种。 */}
                    {parts.hidden.length > 0 && `；已隐藏 ${parts.hidden.length} 种`}
                  </span>
                </button>
                {moreOpen && (
                  <>
                    {parts.more.length > 0 && (
                      <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {parts.more.map((item) => (
                          <CatalogCard
                            key={item.type}
                            item={item}
                            running={runningType === item.type}
                            {...cardProps}
                          />
                        ))}
                      </div>
                    )}
                    {parts.hidden.length > 0 && (
                      <div className="mt-4 border-t pt-3">
                        <p className="mb-2 text-2xs text-muted-foreground">
                          你说过不看的（{parts.hidden.length}）—— 想回头就点「恢复显示」
                        </p>
                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                          {parts.hidden.map((item) => (
                            <CatalogCard
                              key={item.type}
                              item={item}
                              running={runningType === item.type}
                              {...cardProps}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}

            <Section
              title="即将开通"
              hint="推荐档照给 —— 但还没接通，按钮就不摆在那儿骗你点"
              items={parts.comingSoon}
              running={runningType}
              cardProps={cardProps}
            />

            <PushSettings />
          </>
        )}
      </div>

      <KeywordsDrawer
        item={editing}
        products={products}
        open={!!editing}
        saving={saving}
        onOpenChange={(o) => !o && setEditing(null)}
        onSave={(k, r, t) => void saveKeywords(k, r, t)}
      />
      <SubscribeDialog
        item={subscribing}
        // ★ 契约 §17.3.1:产品页签下订的是这个产品 —— 确认框里必须说出来订的是谁。
        productName={product ? nameOf(product) : null}
        price={subscribing?.priceCredits ?? null}
        saving={saving}
        onOpenChange={(o) => !o && setSubscribing(null)}
        onConfirm={(c) => void confirmSubscribe(c)}
      />
    </AcqShell>
  );
}

function Section({
  title,
  hint,
  items,
  running,
  cardProps,
}: {
  title: string;
  hint: string;
  items: AcqCatalogItem[];
  running: string | null;
  cardProps: CardProps;
}) {
  if (items.length === 0) return null;
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <h2 className="text-sm font-bold">{title}</h2>
        <span className="text-2xs text-muted-foreground">{hint}</span>
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <CatalogCard key={item.type} item={item} running={running === item.type} {...cardProps} />
        ))}
      </div>
    </section>
  );
}

/** 每张卡都要的那几件事(除了卡自己那一项)。 */
interface CardProps {
  onRun: (type: string) => void;
  /** 正在跑的那一轮已经等了几秒(0927 异步化)。只有 `running` 的那张卡拿它画「已等 m:ss」。 */
  elapsedSec: number;
  /** 今日商机已满的那句人话(1006);null = 没满 / 不知道。 */
  todayFull: string | null;
  productName: (code: string) => string;
  onEditKeywords: (item: AcqCatalogItem) => void;
  onToggleSubscribe: (item: AcqCatalogItem, next: boolean) => void;
  onSetLevel: (item: AcqCatalogItem, level: AcqPrefLevel) => void;
  onSetHidden: (item: AcqCatalogItem, hidden: boolean) => void;
  /** 只改选中产品下的档(契约 §17.2)。 */
  onSetProductLevel: (item: AcqCatalogItem, level: AcqPrefLevel) => void;
  /** 整份场景表(契约 §16.1)。卡要显示哪几个由这里按产品算,不进卡。 */
  scenes: AcqScene[];
  activeScene: string | null;
  focusProduct: string;
}

/**
 * 把「这一页此刻停在哪个产品 / 哪个场景上」翻译成一张卡要的三样东西。
 *
 * ★★ 判据只放**一处**:卡自己不知道页签,页面也不在三个地方各算一遍。
 *   上一版这张卡在四处被渲染(主推 / 可试 / 更多 / 已隐藏),
 *   任何一处漏传一个 prop,失败形态都是"某一区的卡少一行字"——静默、且只在那一区。
 */
function CatalogCard({
  item,
  running,
  elapsedSec,
  scenes,
  activeScene,
  focusProduct,
  ...rest
}: CardProps & { item: AcqCatalogItem; running: boolean }) {
  const mine = scenesOf(item, scenes, focusProduct);
  return (
    <DiscoveryTypeCard
      item={item}
      elapsedSec={running ? elapsedSec : null}
      running={running}
      {...rest}
      scenes={mine}
      highlighted={activeScene != null && mine.some((s) => s.id === activeScene)}
      focusProduct={focusProduct}
      focusWords={focusProduct ? productKeywords(item, focusProduct) : undefined}
    />
  );
}

/**
 * 「在「X」下已隐藏 N 种 · 恢复」(契约 §18.1-2)。
 *
 * ═══ 为什么是一行小字 + 一个弹出的小列表 ═══════════════════════════════════
 * ★★ 「真隐藏」与「找得回来」是同一件事的两半:卡不渲染是用户要的(他说了不看),
 *   但**一个没有出口的隐藏等于删掉** —— 他记得的只是"我好像关过它",
 *   不记得关的是哪一种,更不知道去哪儿翻。所以这一行必须**在页签正下方**、
 *   在他做那个动作的同一个地方,而不是折进「更多」里(那是全局隐藏的地盘)。
 * ★ 数摆在前面、动作跟在后面:这一行八成的时候只是"报个数",
 *   真要恢复才点开 —— 把 N 张卡直接平铺出来等于没隐藏。
 * ★ 逐个恢复不给「全部恢复」:一次藏了 5 种的人多半只想要回其中一种,
 *   而"全部恢复"点错了没有撤销。
 */
function ProductHiddenBar({
  productName,
  items,
  onRestore,
  emptyPage = false,
}: {
  productName: string;
  items: AcqCatalogItem[];
  onRestore: (item: AcqCatalogItem) => void;
  /** 下面一张卡都不剩了 ⇒ 这一行是整页唯一的解释,要说成整话。 */
  emptyPage?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    // ★ `div` 不是 `p`:里面有 PopoverTrigger(button)与一个会被 Portal 搬走的列表,
    //   用段落标签只会给将来某次改动留一个"块级元素塞进 p"的坑。
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-1.5 text-2xs text-muted-foreground",
        emptyPage && "rounded-lg border border-dashed bg-muted/30 px-3 py-2 text-xs"
      )}
    >
      <EyeOff className="h-3 w-3 shrink-0" aria-hidden />
      <span>
        在「{productName}」下已隐藏 {items.length} 种
        {emptyPage && "（就是全部）—— 所以下面什么都没有，不是页面坏了"}
      </span>
      <span aria-hidden>·</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="font-semibold text-primary hover:underline">
            恢复
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2">
          <p className="px-1 pb-1.5 text-2xs text-muted-foreground">
            你说过在「{productName}」下不看的 —— 点一下放回来，这条线重新按场景排。
          </p>
          <ul
            aria-label={`你在「${productName}」下说过不看的那几种`}
            className="max-h-64 space-y-0.5 overflow-y-auto"
          >
            {items.map((i) => (
              <li key={i.type} className="flex items-center gap-2 rounded px-1 py-1 hover:bg-muted">
                <span className="min-w-0 flex-1 truncate text-xs">{i.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 shrink-0 px-2 text-2xs"
                  // ★ 可及名要带上是哪一种:五行按钮全叫「放回来」的话,读屏的人
                  //   与测试都只能靠视觉位置去分辨,而那正是最容易改坏的一格。
                  aria-label={`把「${i.name}」放回来`}
                  onClick={() => onRestore(i)}
                >
                  <Undo2 className="h-3 w-3" />
                  放回来
                </Button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * 「这个产品一条场景都没读出来」的兜底(契约 §17.2 console 那一条)。
 *
 * ═══ 为什么必须有这一块 ═══════════════════════════════════════════════════
 * ★★ 根因是实证过的(演示机销帮):5 个在售产品里 `zhinao` / `aixin` 场景 0、
 *   `keywordsByProduct` 也 0 —— 因为这两个产品的资料是**产品介绍**不是问答,
 *   检索捞不回段落,模型没材料。这时候按产品重算的结果是 **17 张卡全落「更多」**:
 *   用户点开一个产品页签,看到的是一片空白加一个折叠区,没有任何一处说为什么。
 * ★ 所以这块要说三件事:**为什么空**(读不出场景,不是坏了)、**现在能干什么**
 *   (按行业通用推荐看)、**怎么让它不空**(让 AI 只为这个产品再推一次,免费)。
 * ★ 「免费」两个字写在按钮上:推断确实不花钱,而用户在这一版里刚被收过一次钱
 *   (追爆仓换词),不写清他不会按。
 */
function ProductNoScenes({
  productName,
  globalFallback,
  recommending,
  onSeeGlobal,
  onBackToProduct,
  onRecommendProduct,
}: {
  productName: string;
  globalFallback: boolean;
  recommending: boolean;
  onSeeGlobal: () => void;
  onBackToProduct: () => void;
  onRecommendProduct: () => void;
}) {
  return (
    <section className="rounded-xl border border-dashed bg-muted/30 px-4 py-3">
      <p className="text-xs leading-relaxed">
        <span className="font-semibold">没有从你的资料里读出「{productName}」的获客场景。</span>
        <span className="text-muted-foreground">
          {" "}
          多半是这个产品的资料是「产品介绍」而不是问答 —— 检索捞不到「谁会需要、什么时候需要」， AI
          就没有材料可推。下面的分区因此只能按词组排。
        </span>
      </p>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {globalFallback ? (
          <Button variant="outline" size="sm" onClick={onBackToProduct}>
            回到按这个产品看
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={onSeeGlobal}>
            按行业通用推荐看
          </Button>
        )}
        <Button size="sm" onClick={onRecommendProduct} disabled={recommending}>
          {recommending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          让 AI 只为这个产品再推一次（免费）
        </Button>
        <span className="text-2xs text-muted-foreground">
          只动这个产品的场景和词组，别的产品不受影响
        </span>
      </div>
    </section>
  );
}
